import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, useLanguage } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { BookOpen, Mail, Lock, User, Loader2, Eye, EyeOff } from "lucide-react";

const Login = () => {
  const navigate = useNavigate();
  const { loginWithGoogle, loginWithEmail, user } = useAuth();
  const { language } = useLanguage();
  const isEnglish = language === "en";
  const googleIconUrl = `${process.env.PUBLIC_URL}/icons/google.svg`;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      navigate("/home", { replace: true });
    }
  }, [user, navigate]);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await loginWithEmail(email, password);
      navigate("/home", { replace: true });
    } catch (error) {
      // Error handled in auth context
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-islamic-teal to-islamic-navy flex flex-col">
      {/* Hero Section */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 text-white text-center">
        <div className="w-20 h-20 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center mb-6">
          <BookOpen className="w-10 h-10" />
        </div>
        <h1 className="text-3xl font-bold mb-2">Quran Companion</h1>
        <p className="text-white/80 text-lg max-w-xs">
          {isEnglish ? "Read, memorize, and learn the Quran with ease" : "Baca, hafal, dan pelajari Al-Quran dengan mudah"}
        </p>
      </div>

      {/* Login Card */}
      <div className="bg-background rounded-t-3xl px-6 py-8 space-y-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold">{isEnglish ? "Welcome Back" : "Selamat Datang"}</h2>
          <p className="text-muted-foreground text-sm mt-1">{isEnglish ? "Sign in to continue" : "Masuk untuk melanjutkan"}</p>
        </div>

        {/* Google Login */}
        <Button
          onClick={loginWithGoogle}
          variant="outline"
          className="w-full h-14 text-base font-medium gap-3"
          data-testid="google-login-btn"
        >
          <img src={googleIconUrl} alt="Google" className="w-5 h-5" />
          {isEnglish ? "Continue with Google" : "Masuk dengan Google"}
        </Button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">{isEnglish ? "or" : "atau"}</span>
          </div>
        </div>

        {/* Email Login Form */}
        <form onSubmit={handleEmailLogin} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                placeholder={isEnglish ? "name@email.com" : "nama@email.com"}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 h-12"
                required
                data-testid="email-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 pr-10 h-12"
                required
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label={showPassword ? (isEnglish ? "Hide password" : "Sembunyikan password") : (isEnglish ? "Show password" : "Tampilkan password")}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <Button 
            type="submit" 
            className="w-full h-12 text-base font-medium"
            disabled={loading}
            data-testid="login-submit-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              isEnglish ? "Sign In" : "Masuk"
            )}
          </Button>
        </form>

        <p className="text-center text-sm text-muted-foreground">
          {isEnglish ? "Don't have an account? " : "Belum punya akun? "}
          <Link to="/register" className="text-primary font-medium hover:underline" data-testid="register-link">
            {isEnglish ? "Register" : "Daftar"}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
